/**
 * Cart Progress Bar – Web Component
 *
 * Multi-tier progress bar that tracks cart total against configurable
 * thresholds for Free Shipping, Free Gift, and Expedited Shipping.
 *
 * Cart total excludes:
 *  - Free-gift line items (_free_gift property)
 *  - Out-of-stock items (variant not available)
 *  - Optionally: gift cards and digital products (via data attributes)
 *
 * Reacts to `cart:update` events dispatched by the theme's cart system.
 */

/** @typedef {{ name: string, threshold: number }} Tier */

/**
 * @typedef {Object} CartLineItem
 * @property {number}  line_price
 * @property {number}  variant_id
 * @property {string}  [key]
 * @property {boolean} [gift_card]
 * @property {boolean} [requires_shipping]
 * @property {string}  [product_type]
 * @property {Record<string, string>} [properties]
 */

/**
 * @typedef {Object} CartPayload
 * @property {CartLineItem[]} items
 * @property {number}         item_count
 * @property {number}         total_price
 */

const DEBOUNCE_MS = 200;
const CURRENCY_FMT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const CURRENCY_FMT_SHORT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Global lock shared across all CartProgressBar instances to prevent race conditions. */
let _giftManagementLock = false;

class CartProgressBar extends HTMLElement {
  /** @type {Tier[]} */
  #tiers = [];
  /** @type {number|null} */
  #lastTotal = null;
  /** @type {number|null} */
  #debounceTimer = null;
  /** @type {boolean} */
  #isRefreshing = false;
  /** When true, variant-selector is mid-swap; skip gift management. */
  _externalSwapInProgress = false;

  connectedCallback() {
    this.#lastTotal = null;
    this.#parseTiers();
    this.#bindEvents();
    this.#scheduleRefresh();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#onCartUpdate);
    document.removeEventListener('cart:synced', this.#onCartGeneric);
    document.removeEventListener('cart:refresh', this.#onCartGeneric);
    document.removeEventListener('quantity-selector:update', this.#onCartGeneric);
    document.removeEventListener('cart-drawer:open', this.#onCartGeneric);
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------------

  #parseTiers() {
    const raw = this.dataset.tiers || '';
    if (!raw) return;

    this.#tiers = raw.split(',').map((pair) => {
      const [name, value] = pair.split(':');
      // @ts-ignore
      return { name: name.trim(), threshold: parseFloat(value) || 0 };
    }).filter((t) => t.threshold > 0);

    this.#tiers.sort((a, b) => a.threshold - b.threshold);
  }

  #bindEvents() {
    document.addEventListener('cart:update', this.#onCartUpdate);
    document.addEventListener('cart:synced', this.#onCartGeneric);
    document.addEventListener('cart:refresh', this.#onCartGeneric);
    document.addEventListener('quantity-selector:update', this.#onCartGeneric);
    document.addEventListener('cart-drawer:open', this.#onCartGeneric);
  }

  // ---------------------------------------------------------------------------
  // Event handlers (arrow fields for stable `this` binding)
  // ---------------------------------------------------------------------------

  /** @param {Event} e */
  #onCartUpdate = (e) => {
    const detail = /** @type {CustomEvent} */ (e).detail;
    const source = detail?.data?.source || detail?.sourceId;
    if (source === 'cart-progress-bar' || source === 'free-gift-variant-selector') return;
    this.#scheduleRefresh();
  };

  #onCartGeneric = () => {
    this.#scheduleRefresh();
  };

  #scheduleRefresh() {
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#refresh(), DEBOUNCE_MS);
  }

  // ---------------------------------------------------------------------------
  // Core: fetch cart and compute
  // ---------------------------------------------------------------------------

  async #refresh() {
    if (this.#isRefreshing || !this.#tiers.length) return;
    this.#isRefreshing = true;
    try {
      const cart = await this.#fetchCart();
      if (!cart) return;
      const total = this.#computeEligibleTotal(cart);
      if (this.#hasOnlyGiftCards(cart)) {
        this.style.display = 'none';
        this.#lastTotal = null;
        this.#handleFreeGift(cart, total);
        return;
      }
      this.style.display = '';

      if (this.#lastTotal !== null && Math.abs(total - this.#lastTotal) < 0.01) return;

      this.#lastTotal = total;
      this.#render(total);
      this.#handleFreeGift(cart, total);
    } catch {
      // Silently fail – bar stays at last known state
    } finally {
      this.#isRefreshing = false;
    }
  }

  /** @returns {Promise<CartPayload|null>} */
  async #fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) return null;
    return res.json();
  }

  // ---------------------------------------------------------------------------
  // Total calculation – excludes out-of-stock, free gifts, optionally gift cards / digital
  // ---------------------------------------------------------------------------

  /**
   * @param {CartPayload} cart
   * @returns {number} Dollar total
   */
  #computeEligibleTotal(cart) {
    const includeGiftCards = this.dataset.includeGiftCards !== 'false';
    const includeDigital = this.dataset.includeDigital === 'true';
    const includeSecureShield = true; // TODO: make manageable from theme settings
    let total = 0;

    for (const item of cart.items) {

      let itemPrice = item.line_price;
      if(document.querySelector(`.bv-cart-item[data-variant-id="${item.variant_id}"]`) && document.querySelector(`.bv-cart-item[data-variant-id="${item.variant_id}"]`).dataset.itemPrice) {
        itemPrice = document.querySelector(`.bv-cart-item[data-variant-id="${item.variant_id}"]`).dataset.itemPrice;
      }

      if(includeSecureShield && item.product_type === 'secure-shield')  {
        total += item.line_price || 0;
      };
      if (this.#isFreeGiftItem(item)) continue;
      if (!includeGiftCards && this.#isGiftCard(item)) continue;
      if (!includeDigital && this.#isDigital(item)) continue;

      // Skip secure shield only if explicitly excluded
      if (!includeSecureShield && item.product_type === 'secure-shield') continue;

      total += Number(itemPrice) || 0;
    }

    return total / 100;
  }

  /** @param {CartLineItem} item */
  #isFreeGiftItem(item) {
    return item.properties?._free_gift === 'true' || item.properties?.['_free_gift'] === 'true';
  }

  /** @param {CartLineItem} item */
  #isGiftCard(item) {
    if (item.gift_card === true) return true;
    const pt = (item.product_type || '').toLowerCase();
    return pt === 'gift card' || pt === 'gift cards';
  }

  /** @param {CartLineItem} item */
  #isDigital(item) {
    return item.requires_shipping === false;
  }

  /**
   * Returns true when every non-free-gift line item in the cart is a gift card.
   * An empty cart also returns true (nothing to show progress for).
   * @param {CartPayload} cart
   * @returns {boolean}
   */
  #hasOnlyGiftCards(cart) {
    const physicalItems = cart.items.filter(
      (item) => !this.#isFreeGiftItem(item) && !this.#isGiftCard(item)
    );
    return physicalItems.length === 0;
  }

  // ---------------------------------------------------------------------------
  // Social campaign session → free shipping boost
  // ---------------------------------------------------------------------------

  /**
   * True when social-campaign free shipping is enabled and a campaign
   * value exists. If a param list is configured, the value must match it;
   * an empty list allows any campaign src.
   * @returns {boolean}
   */
  #hasCampaignSession() {
    const config = /** @type {{
      paramName?: string,
      sessionEnabled?: boolean,
      freeShippingEnabled?: boolean,
      freeShippingParams?: string,
    }} */ (window.SocialCampaignConfig || {});

    if (!config.freeShippingEnabled) return false;
    if (config.sessionEnabled === false) return false;

    const allowedParams = (config.freeShippingParams || '')
      .split(',')
      .map((val) => val.toLowerCase().trim())
      .filter((val) => val !== '');

    const paramName = config.paramName || 'utm_source';
    let campaignValue = '';

    const cookieName = `shoplc_campaign_${paramName}=`;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        campaignValue = decodeURIComponent(cookie.substring(cookieName.length));
        break;
      }
    }

    if (!campaignValue) {
      try {
        campaignValue = new URLSearchParams(window.location.search).get(paramName) || '';
      } catch {
        // Ignore malformed URL
      }
    }

    if (!campaignValue) return false;
    if (!allowedParams.length) return true;

    return allowedParams.includes(campaignValue.toLowerCase().trim());
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  /**
   * Determines which tiers are unlocked and renders progress, icons, message.
   * The green bar only spans up to the highest non-expedited tier.
   * Expedited is message-only — it has no icon or fill representation on the bar.
   * When a social campaign session is active, free shipping is treated as unlocked
   * and the meter is filled at least to the shipping threshold.
   * @param {number} cartTotal
   */
  #render(cartTotal) {
    const campaignShippingBoost = this.#hasCampaignSession();
    this.classList.toggle('cart-progress-bar--campaign-session', campaignShippingBoost);
    if(campaignShippingBoost) {
      // change the message for the social campaign shipping boost
      const target = 'freeshippingonordersover$50';
      document.querySelectorAll('.bv-why-text').forEach((el) => {
        const text = el.textContent.replace(/\s+/g, '').toLowerCase();

        if (text === target) {
          el.innerHTML = '<p>Free Shipping<br>on <strong>all orders</strong></p>';
        }
      });
    }

    const tierStates = this.#tiers.map((tier) => {
      const unlocked =
        (campaignShippingBoost && tier.name === 'shipping') ||
        cartTotal >= tier.threshold;
      return {
        ...tier,
        unlocked,
        remaining: unlocked ? 0 : Math.max(0, tier.threshold - cartTotal),
      };
    });

    const barTiers = this.#tiers.filter((t) => t.name !== 'expedited');
    const maxThreshold = barTiers[barTiers.length - 1]?.threshold || 1;

    let fillTotal = cartTotal;
    if (campaignShippingBoost) {
      const shippingTier = this.#tiers.find((t) => t.name === 'shipping');
      if (shippingTier) {
        fillTotal = Math.max(cartTotal, shippingTier.threshold);
      }
    }
    const percent = Math.min(100, Math.max(0, (fillTotal / maxThreshold) * 100));

    this.#renderFill(percent);
    this.#renderIcons(tierStates.filter((t) => t.name !== 'expedited'), maxThreshold);
    this.#renderMessage(tierStates);
  }

  /** @param {number} percent */
  #renderFill(percent) {
    const fill = this.querySelector('[ref="fill"]');
    const track = this.querySelector('[ref="track"]');
    if (fill instanceof HTMLElement) {
      fill.style.width = `${percent}%`;
    }
    if (track instanceof HTMLElement) {
      track.setAttribute('aria-valuenow', String(Math.round(percent)));
    }
  }

  /**
   * @param {Array<{name: string, threshold: number, unlocked: boolean}>} tierStates
   * @param {number} maxThreshold
   */
  #renderIcons(tierStates, maxThreshold) {
    for (const tier of tierStates) {
      const refName = `icon${tier.name.charAt(0).toUpperCase()}${tier.name.slice(1)}`;
      const el = this.querySelector(`[ref="${refName}"]`);
      if (!(el instanceof HTMLElement)) continue;

      const posPercent = Math.max(2, Math.min(100, (tier.threshold / maxThreshold) * 100));
      el.style.left = `${posPercent}%`;
      el.style.transform = `translateX(-${posPercent}%)`;
      el.classList.toggle('cart-progress-bar__icon-wrap--unlocked', tier.unlocked);

      const label = el.querySelector('.cart-progress-bar__icon-label');
      if (label instanceof HTMLElement) {
        label.textContent = CURRENCY_FMT_SHORT.format(tier.threshold);
      }
    }
  }

  /**
   * Build the appropriate message based on which tiers are unlocked.
   * @param {Array<{name: string, threshold: number, unlocked: boolean, remaining: number}>} tierStates
   */
  #renderMessage(tierStates) {
    const msgEl = this.querySelector('[ref="message"]');
    if (!(msgEl instanceof HTMLElement)) return;

    const firstLocked = tierStates.find((t) => !t.unlocked);
    const allUnlocked = tierStates.every((t) => t.unlocked);
    const unlockedNames = tierStates.filter((t) => t.unlocked).map((t) => t.name);

    let html = '';

    if (allUnlocked) {
      if(unlockedNames.length === 1) {
        if(unlockedNames[0] === 'expedited') {
          html = this.#getMessage('expedited-locked');
        } else if(unlockedNames[0] === 'shipping') {
          html = this.#getMessage('shipping-unlocked');
        } else {
          // @ts-ignore
          html = this.#getMessage(unlockedNames[0]);
        }
      } else if(unlockedNames.length == 2) {
        html = this.#getMessage(unlockedNames.join('-'));
      } else {
        html = this.#getMessage('all-unlocked');
      }
    } else if (!firstLocked) {
      html = this.#getMessage('all-unlocked');
    } else if (firstLocked.name === 'shipping') {
      html = this.#fillAmount(this.#getMessage('shipping'), firstLocked.remaining);
    } else if (firstLocked.name === 'gift' && unlockedNames.includes('shipping')) {
      html = this.#fillAmount(this.#getMessage('gift'), firstLocked.remaining);
    } else if (firstLocked.name === 'expedited') {
      if (unlockedNames.length === 0) {
        const shippingTier = tierStates.find((t) => t.name === 'shipping');
        if (shippingTier && !shippingTier.unlocked) {
          html = this.#fillAmount(this.#getMessage('shipping'), shippingTier.remaining);
        } else {
          html = this.#fillAmount(this.#getMessage('expedited-unlocked'), firstLocked.remaining);
        }
      } else {
        if(unlockedNames.includes('shipping') && unlockedNames.includes('gift')) {
          html = this.#fillAmount(this.#getMessage('expedited'), firstLocked.remaining);
        } else if(unlockedNames.includes('shipping')) {
          // Option A: campaign session keeps shipping-only unlocked message
          // until cart reaches the expedited threshold (then normal expedited msgs).
          if (this.#hasCampaignSession()) {
            html = this.#getMessage('shipping-unlocked');
          } else {
            html = this.#fillAmount(this.#getMessage('shipping-expedited-locked'), firstLocked.remaining);
          }
        } else {
          html = this.#fillAmount(this.#getMessage('expedited-unlocked'), firstLocked.remaining);
        }
      }
    } else if (unlockedNames.length > 0 && !firstLocked) {
      html = this.#buildUnlockedMessage(unlockedNames);
    } else if (unlockedNames.length > 0) {
      html = this.#fillAmount(this.#getMessageForTier(firstLocked.name), firstLocked.remaining);
    } else {
      html = this.#fillAmount(this.#getMessage('shipping'), firstLocked.remaining);
    }

    if (!html.trim()) {
      html = this.#getMessage('all-unlocked');
    }

    msgEl.innerHTML = this.#sanitizeMessage(html);
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #getMessage(key) {
    const map = {
      'shipping': this.dataset.msgShipping || 'Spend ${{amount}} more for Free Delivery!',
      'gift': this.dataset.msgGift || 'Spend ${{amount}} more to unlock a Free Gift!',
      'expedited': this.dataset.msgExpedited || 'Spend ${{amount}} more for Expedited Shipping!',
      'shipping-unlocked': this.dataset.msgShippingUnlocked || "Hooray! You've unlocked <strong>Free Delivery!</strong>",
      'expedited-unlocked': this.dataset.msgExpeditedUnlocked || "Spend ${{amount}} more for <strong>Expedited Shipping!</strong>",
      'expedited-locked': this.dataset.msgExpeditedLocked || "🎉 Hooray! You've unlocked <strong>Expedited Shipping!</strong>",
      'shipping-gift': this.dataset.msgShippingGiftUnlocked || "🎉 Hooray! You've unlocked <strong>Free Delivery & Free Gift!</strong>", // TODO: Add this message dynamically
      'shipping-expedited': this.dataset.msgShippingExpeditedUnlocked || "🎉 Hooray! You've unlocked <strong>Free Delivery & Expedited Shipping!</strong>",
      'shipping-expedited-locked': this.dataset.msgShippingExpeditedLocked || "🎉 Hooray! You've unlocked <strong>Free Delivery!</strong> Spend <strong>${{amount}}</strong> more for <strong>Expedited Shipping!</strong>",
      'gift-expedited': this.dataset.msgGiftExpeditedUnlocked || "🎉 Hooray! You've unlocked <strong>Free Gift & Expedited Shipping!</strong>",
      'all-unlocked': this.dataset.msgAllUnlocked || "You've unlocked all rewards!",
    };
    // @ts-ignore
    return map[key].replace("{{TRUCK_ICON}}", '<svg width="24" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.4894 3.91379C20.3526 3.74211 20.146 3.6053 19.9394 3.6053H15.5109V0.686723C15.5109 0.308476 15.2024 0 14.8242 0H1.67336C1.29513 0 0.986656 0.30849 0.986656 0.686723C0.986656 1.06497 1.29514 1.37345 1.67336 1.37345H14.1375V13.2544L6.58367 13.2531C6.30872 12.4631 5.55361 11.8797 4.66034 11.8797C3.76707 11.8797 3.01196 12.4631 2.73701 13.2531H1.67206C1.29382 13.2531 0.985352 13.5616 0.985352 13.9398C0.985352 14.3181 1.29383 14.6266 1.67206 14.6266H2.73701C3.01196 15.4166 3.76707 16 4.66034 16C5.55361 16 6.30871 15.4166 6.58367 14.6266H16.816C17.0909 15.4166 17.846 16 18.7393 16C19.6326 16 20.3877 15.4166 20.6626 14.6266H22.7214C23.0996 14.6266 23.4081 14.3181 23.4081 13.9398V8.30939C23.4081 8.17258 23.3732 8.03443 23.2713 7.93114L20.4894 3.91379ZM19.5626 4.97876L21.3826 7.62237H15.5106V4.97876H19.5626ZM20.7294 13.2531C20.4545 12.4631 19.6994 11.8796 18.8061 11.8796C17.9128 11.8796 17.1577 12.4631 16.8828 13.2531H15.5107V8.99609H22.0344V13.2531H20.7294Z" fill="black"/><path d="M6.34867 9.03047C6.34867 8.65223 6.04019 8.34375 5.66196 8.34375H1.06171C0.683469 8.34375 0.375 8.65224 0.375 9.03047C0.375 9.40872 0.683483 9.7172 1.06171 9.7172H5.66196C6.0402 9.7172 6.34867 9.40737 6.34867 9.03047Z" fill="black"/><path d="M0.686708 5.5629H5.28696C5.6652 5.5629 5.97367 5.25441 5.97367 4.87618C5.97367 4.49794 5.66519 4.18945 5.28696 4.18945H0.686708C0.308469 4.18945 0 4.49794 0 4.87618C0 5.25441 0.308483 5.5629 0.686708 5.5629Z" fill="black"/></svg>') || '';
  }

  /** @param {string} tierName @returns {string} */
  #getMessageForTier(tierName) {
    return this.#getMessage(tierName);
  }

  /**
   * @param {string} template
   * @param {number} amount
   * @returns {string}
   */
  #fillAmount(template, amount) {
    return template.replace(/\{\{amount\}\}/g, CURRENCY_FMT.format(amount).replace('$', ''));
  }

  /** @param {string[]} names @returns {string} */
  #buildUnlockedMessage(names) {
    const labels = names.map((n) => {
      if (n === 'shipping') return 'Free Delivery';
      if (n === 'gift') return 'Free Gift';
      if (n === 'expedited') return 'Free Expedited Shipping';
      return n;
    });
    return `Hooray! You've unlocked <strong>${labels.join(' & ')}</strong>!`;
  }

  /**
   * Allow only <strong>, <em>, <span> – strip everything else.
   * @param {string} html
   * @returns {string}
   */
  #sanitizeMessage(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.innerHTML;
  }

  // ---------------------------------------------------------------------------
  // Free Gift management
  // ---------------------------------------------------------------------------

  /**
   * @param {CartPayload} cart
   * @param {number} cartTotal
   */
  async #handleFreeGift(cart, cartTotal) {
    const variantId = this.dataset.giftVariantId;
    const giftTier = this.#tiers.find((t) => t.name === 'gift');
    // Check if gift tier is defined
    if (!giftTier) {
      // @ts-ignore
      await this.#removeFreeGift(cart, variantId);
      this.#dispatchCartUpdate();
      return;
    }
    
    if (!variantId || !giftTier || _giftManagementLock || this._externalSwapInProgress) return;

    const eligible = cartTotal >= giftTier.threshold;
    const inCart = this.#isGiftVariantInCart(cart, variantId);
    
    if (eligible && !inCart) {
      await this.#addFreeGift(variantId, giftTier.threshold);
    } else if (!eligible && inCart) {
      await this.#removeFreeGift(cart, variantId);
    } else if (eligible && inCart) {
      const configuredId = parseInt(variantId, 10);
      const mismatch = cart.items.some(
        (item) => this.#isFreeGiftItem(item) && item.variant_id !== configuredId
      );
      if (mismatch) {
        await this.#swapFreeGift(cart, variantId, giftTier.threshold);
      } else {
        await this.#enforceGiftQuantity(cart);
      }
    }
  }

  /**
   * Check if ANY free gift line exists in the cart (regardless of variant).
   * This prevents the progress bar from re-adding the default variant when
   * the customer has swapped to a different one.
   * @param {CartPayload} cart
   * @param {string} _variantId - Kept for API compat, not used for detection
   * @returns {boolean}
   */
  #isGiftVariantInCart(cart, _variantId) {
    return cart.items.some((item) => this.#isFreeGiftItem(item));
  }

  /** @param {string} variantId @param {number} threshold */
  async #addFreeGift(variantId, threshold) {
    if (_giftManagementLock) return;
    _giftManagementLock = true;
    try {
      const cart = await this.#fetchCart();
      if (!cart) return;

      const existingGifts = cart.items.filter((item) => this.#isFreeGiftItem(item));
      
      if (existingGifts.length > 0) {
        /** @type {Record<string, number>} */
        const updates = {};
        for (const item of existingGifts) {
          if (item.key) updates[item.key] = 0;
        }
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }

      await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            '_free_gift': 'true',
            '_gift_message': 'Free Gift - Thank you for your purchase!',
          },
        }),
      });

      this.#dispatchCartUpdate();
    } catch {
      // Fail silently
    } finally {
      setTimeout(() => { _giftManagementLock = false; }, 400);
    }
  }

  /**
   * Remove the old gift variant and add the currently configured one.
   * Handles the case where the backend changes the gift product/variant
   * while an older gift item is already in the cart.
   * @param {CartPayload} cart
   * @param {string} variantId - The newly configured variant
   * @param {number} threshold
   */
  async #swapFreeGift(cart, variantId, threshold) {
    if (_giftManagementLock) return;
    _giftManagementLock = true;
    try {
      const giftItems = cart.items.filter((item) => this.#isFreeGiftItem(item));
      if (giftItems.length) {
        /** @type {Record<string, number>} */
        const updates = {};
        for (const item of giftItems) {
          if (item.key) updates[item.key] = 0;
        }
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }

      await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            '_free_gift': 'true',
            '_gift_message': 'Free Gift - Thank you for your purchase!',
          },
        }),
      });

      this.#dispatchCartUpdate();
    } catch {
      // Fail silently
    } finally {
      setTimeout(() => { _giftManagementLock = false; }, 400);
    }
  }

  /**
   * Remove ANY free gift line from cart (regardless of which variant it is).
   * @param {CartPayload} cart
   * @param {string} _variantId - Kept for API compat
   */
  async #removeFreeGift(cart, _variantId) {
    if (_giftManagementLock) return;
    _giftManagementLock = true;
    try {
      const giftItems = cart.items.filter((item) => this.#isFreeGiftItem(item));
      if (!giftItems.length) return;

      /** @type {Record<string, number>} */
      const updates = {};
      for (const item of giftItems) {
        if (item.key) updates[item.key] = 0;
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      this.#dispatchCartUpdate();
    } catch {
      // Fail silently
    } finally {
      setTimeout(() => { _giftManagementLock = false; }, 400);
    }
  }

  /**
   * If the free gift exists but its quantity exceeds 1, correct it.
   * Also consolidates duplicate free gift lines into a single qty-1 entry.
   * @param {CartPayload} cart
   */
  async #enforceGiftQuantity(cart) {
    const giftItems = cart.items.filter((item) => this.#isFreeGiftItem(item));
    // @ts-ignore
    const needsFix = giftItems.length > 1 || giftItems.some((item) => (item.quantity || 1) > 1);
    if (!needsFix || _giftManagementLock) return;

    _giftManagementLock = true;
    try {
      const keepItem = giftItems[0];
      /** @type {Record<string, number>} */
      const updates = {};

      for (const item of giftItems) {
        // @ts-ignore
        if (item.key === keepItem.key) {
          // @ts-ignore
          updates[item.key] = 1;
        } else if (item.key) {
          updates[item.key] = 0;
        }
      }

      await fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });

      this.#dispatchCartUpdate();
    } catch {
      // Fail silently
    } finally {
      setTimeout(() => { _giftManagementLock = false; }, 400);
    }
  }

  #dispatchCartUpdate() {
    // TODO: Remove this due to many of them listnen cart:update and product-form will call cart.js
    // document.dispatchEvent(new CustomEvent('cart:update', {
    //   detail: { data: { source: 'cart-progress-bar' } },
    // }));
    document.dispatchEvent(new CustomEvent('cart:synced', {
      bubbles: true,
      detail: { source: 'cart-progress-bar' },
    }));
  }
}

if (!customElements.get('cart-progress-bar')) {
  customElements.define('cart-progress-bar', CartProgressBar);
}
